import { RoomPassengers } from "../../types/hotels";

export const DEFAULT_HOTEL_BLOCK_SEARCH_DAYS_BEFORE = 1;

export type HotelBlockSearchDaysBeforeSettings =
  | {
      blockSearchDaysBefore?: string | number | null;
    }
  | null
  | undefined;

export function getHotelBlockSearchDaysBefore(
  hotelSearchSettings: HotelBlockSearchDaysBeforeSettings,
  fallback = DEFAULT_HOTEL_BLOCK_SEARCH_DAYS_BEFORE
): number {
  const value = hotelSearchSettings?.blockSearchDaysBefore;

  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  const days = Number(value);

  return Number.isInteger(days) && days >= 0 ? days : fallback;
}

export function getSearchFormDefaultDepartureDays(
  isHotelSearchForm: boolean,
  departureDays: number,
  hotelSearchSettings: HotelBlockSearchDaysBeforeSettings
): number {
  return isHotelSearchForm
    ? getHotelBlockSearchDaysBefore(hotelSearchSettings)
    : departureDays;
}

/**
 * Calculates the total number of people in a hotel room.
 * Includes adults (ADT), children (CHD), guests, and CBT travellers assigned to the room.
 *
 * @param room - The room object containing ADT, CHD, and guests counts
 * @param roomIndex - The index of the room (used to look up CBT assignments)
 * @param hotelRoomAssignments - Optional CBT hotel room assignments object.
 *   Can be an object with roomIndex keys and either:
 *   - Array of traveller IDs: string[]
 *   - Object with traveller IDs: Record<string, string>
 * @returns The total number of people in the room
 */
export function getRoomTotalPeople(
  room: RoomPassengers,
  roomIndex: number,
  hotelRoomAssignments?: {
    [roomIndex: number]: string[] | { [travelerIndex: number]: string };
  } | null
): number {
  const roomAssignment = hotelRoomAssignments?.[roomIndex];
  let cbtTravellersCount = 0;

  if (Array.isArray(roomAssignment)) {
    cbtTravellersCount = roomAssignment.filter(Boolean).length;
  } else if (roomAssignment && typeof roomAssignment === "object") {
    cbtTravellersCount = Object.values(roomAssignment).filter(Boolean).length;
  }

  const roomTotal =
    (room.ADT || 0) + (room.CHD || 0) + (room.guests || 0) + cbtTravellersCount;

  return roomTotal;
}

/**
 * Checks if any room has only guests (no travelers, no adults, no children).
 * This is used for validation when allowGuestOnlyReservation is false.
 *
 * @param rooms - Object with roomIndex keys and RoomPassengers values
 * @param hotelRoomAssignments - Optional CBT hotel room assignments object.
 *   Can be an object with roomIndex keys and either:
 *   - Array of traveller IDs: string[]
 *   - Object with traveller IDs: Record<string, string>
 * @returns true if any room has only guests and no travelers/adults/children
 */
export function hasRoomsWithOnlyGuests(
  rooms:
    | { [roomIndex: string]: RoomPassengers }
    | { [roomIndex: number]: RoomPassengers },
  hotelRoomAssignments?: {
    [roomIndex: number]: string[] | { [travelerIndex: number]: string };
  } | null
): boolean {
  const roomEntries = Object.entries(rooms);

  return roomEntries.some(([roomIndex, room]) => {
    const roomAssignment = hotelRoomAssignments?.[Number(roomIndex)];
    let cbtTravellersCount = 0;

    if (Array.isArray(roomAssignment)) {
      cbtTravellersCount = roomAssignment.filter(Boolean).length;
    } else if (roomAssignment && typeof roomAssignment === "object") {
      cbtTravellersCount = Object.values(roomAssignment).filter(Boolean).length;
    }

    const hasGuests = (room.guests || 0) > 0;
    const hasAdults = (room.ADT || 0) > 0;
    const hasChildren = (room.CHD || 0) > 0;
    const hasTravelers = cbtTravellersCount > 0;

    return hasGuests && !hasAdults && !hasChildren && !hasTravelers;
  });
}
